import {
  Button,
  type DialogComponentProps,
  DialogResultStatus,
  type FilterOption,
  type FilterOptionConfig,
} from '@brightdrop/bd-ui';
import { TranslatedLabel, translateText } from '@gm-commercial/translations';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import ChevronDown from '~/assets/icons/new/chevron-down.svg?react';
import BDFilterNestedOptionGroup from '~/common/components/BDFilter/BDFilterNestedOptionGroup';
import type { ElementTypes } from '../../models/common.model';
import CSDateRangeFilter from '../CSDateRangeFilter/CSDateRangeFilter';
import useStyles from './BDFilterModal.styles';

/** Options nested under one parent checkbox inside a filter group. */
export type BDFilterModalNestedOptionGroup<T> = {
  /** Identifies the nested group in test ids. */
  id: string;
  /** Label of the parent checkbox. */
  label: string;
  /** Filter key the nested options write to. */
  type: keyof T;
  /** The options under the parent checkbox. */
  options: FilterOption<ElementTypes<T[keyof T]>>[];
};

export type BDFilterModalOptionConfig<T> = FilterOptionConfig<
  ElementTypes<T[keyof T]>,
  T
> & {
  /**
   * Options nested under parent checkboxes, rendered above the group's own
   * options. Only the accordion layout renders them. Each nested key has to be
   * in the filter entity, since a toggled option is only added to a key the
   * entity already has.
   */
  nestedOptionGroups?: BDFilterModalNestedOptionGroup<T>[];
};

/**
 * Builds the values of a filter group's key and of each nested key, with every
 * option selected or none.
 */
const getGroupSelection = <T,>(
  filterGroup: BDFilterModalOptionConfig<T>,
  isSelected: boolean
) => {
  const valuesOf = (options: { value: unknown }[] = []) =>
    isSelected ? options.map((option) => String(option.value)) : [];

  return {
    [filterGroup.type]: valuesOf(filterGroup.options),
    ...Object.fromEntries(
      (filterGroup.nestedOptionGroups ?? []).map((nestedGroup) => [
        nestedGroup.type,
        valuesOf(nestedGroup.options),
      ])
    ),
  };
};

/** Shape the filter entity is expected to have when `showDateRange` is set. */
type WithDateRange = { dateRange?: [string, string] };
type BDFilterModalProps<T> = Omit<
  DialogComponentProps<T>,
  'onCloseModal' | 'setAlerts'
> & {
  filterOptions: BDFilterModalOptionConfig<T>[];
  defaultState?: T;
  type?: BDFilterModalType;
  showDateRange?: boolean;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  singleColumn?: boolean;
  truncateLongLabels?: boolean;
};

export enum BDFilterModalType {
  SIMPLE = 0,
  ACCORDION = 1,
  DROPDOWN = 2,
}

const BDFilterModal = <T,>({
  entity: filterDialogOptions,
  onResult,
  filterOptions,
  defaultState,
  type = BDFilterModalType.SIMPLE,
  showDateRange = false,
  onDateRangeChange,
  singleColumn = false,
  truncateLongLabels = false,
}: BDFilterModalProps<T>): JSX.Element => {
  const { classes } = useStyles();
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasAutoFocusedRef = useRef(false);

  /**
   * Handles keyboard navigation within filter groups using arrow keys.
   * Supports ArrowUp/ArrowDown/ArrowLeft/ArrowRight for navigation,
   * and Home/End for jumping to first/last option.
   */
  const handleFilterKeyDown = useCallback(
    (event: React.KeyboardEvent, groupId: string) => {
      const container = groupRefs.current.get(groupId);
      if (!container) return;

      const checkboxes = container.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]:not(:disabled)'
      );
      const { activeElement } = document;
      const currentIndex = activeElement
        ? Array.from<Element>(checkboxes).indexOf(activeElement)
        : -1;

      if (currentIndex === -1) return;

      const currentGroupIndex = filterOptions.findIndex(
        (group) => group.id === groupId
      );
      const isAtLastOption = currentIndex === checkboxes.length - 1;
      const isAtFirstOption = currentIndex === 0;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          if (isAtLastOption && currentGroupIndex < filterOptions.length - 1) {
            // Move to first option of next group
            const nextGroupId = filterOptions[currentGroupIndex + 1].id;
            const nextContainer = groupRefs.current.get(nextGroupId);
            const nextCheckbox = nextContainer?.querySelector<HTMLInputElement>(
              'input[type="checkbox"]:not(:disabled)'
            );
            nextCheckbox?.focus();
          } else if (!isAtLastOption) {
            checkboxes[currentIndex + 1].focus();
          }
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          if (isAtFirstOption && currentGroupIndex > 0) {
            // Move to last option of previous group
            const prevGroupId = filterOptions[currentGroupIndex - 1].id;
            const prevContainer = groupRefs.current.get(prevGroupId);
            const prevCheckboxes =
              prevContainer?.querySelectorAll<HTMLInputElement>(
                'input[type="checkbox"]:not(:disabled)'
              );
            if (prevCheckboxes && prevCheckboxes.length > 0) {
              prevCheckboxes[prevCheckboxes.length - 1].focus();
            }
          } else if (!isAtFirstOption) {
            checkboxes[currentIndex - 1].focus();
          }
          break;
        case 'Home':
          event.preventDefault();
          checkboxes[0].focus();
          break;
        case 'End':
          event.preventDefault();
          checkboxes[checkboxes.length - 1].focus();
          break;
      }
    },
    [filterOptions]
  );

  /**
   * Focus the first checkbox when the filter modal opens
   */
  useEffect(() => {
    if (hasAutoFocusedRef.current || filterOptions.length === 0) return;

    const firstGroupId = filterOptions[0].id;
    const container = groupRefs.current.get(firstGroupId);

    // Only focus if the container ref has been set (DOM is rendered)
    if (container) {
      const firstCheckbox = container.querySelector<HTMLInputElement>(
        'input[type="checkbox"]:not(:disabled)'
      );
      if (firstCheckbox) {
        firstCheckbox.focus();
        hasAutoFocusedRef.current = true;
      }
    }
  }, [filterOptions]);

  const saveChange = useCallback(
    (updatedFilterOptions: Partial<T>) => {
      if (onResult) {
        onResult({
          status: DialogResultStatus.SAVE,
          entity: { ...updatedFilterOptions } as T,
        });
      }
    },
    [onResult]
  );

  const onChangeFilter = useCallback(
    (type: keyof T, value: string | null) => {
      if (value) {
        const filterCriteriaSelection = Object.values(
          (filterDialogOptions?.[type as keyof T] || {}) as string[]
        );
        const index = filterCriteriaSelection.indexOf(value);
        let updatedFilterOptions = { ...defaultState } as Partial<T>;
        if (index > -1) {
          const updated = Object.entries(filterDialogOptions || {}).map(
            ([key, items]) => {
              if (type === key) {
                const selectedOption = items as string[];
                const newArray = selectedOption.filter((v) => v !== value);
                return { [key]: newArray };
              }
              return { [key]: items };
            }
          );
          for (const key of updated) {
            updatedFilterOptions = {
              ...updatedFilterOptions,
              ...key,
            };
          }
          saveChange(updatedFilterOptions);
        } else {
          const updated = Object.entries(filterDialogOptions || {}).map(
            ([key, items]) => {
              if (type === key && Array.isArray(items)) {
                return {
                  [type]: [...items, value],
                };
              }
              if (Array.isArray(items)) {
                return {
                  [key]: [...items],
                };
              }
              return undefined;
            }
          );
          for (const key of updated) {
            updatedFilterOptions = {
              ...updatedFilterOptions,
              ...key,
            };
          }
          saveChange(updatedFilterOptions);
        }
      }
    },
    [defaultState, filterDialogOptions, saveChange]
  );

  const handleStartDateChange = useCallback(
    (startDate: string) => {
      const currentDateRange = (filterDialogOptions as WithDateRange)
        ?.dateRange || ['', ''];
      const updatedEntity = {
        ...filterDialogOptions,
        dateRange: [startDate, currentDateRange[1]],
      } as T;
      saveChange(updatedEntity);
      onDateRangeChange?.(startDate, currentDateRange[1]);
    },
    [filterDialogOptions, saveChange, onDateRangeChange]
  );

  const handleEndDateChange = useCallback(
    (endDate: string) => {
      const currentDateRange = (filterDialogOptions as WithDateRange)
        ?.dateRange || ['', ''];
      const updatedEntity = {
        ...filterDialogOptions,
        dateRange: [currentDateRange[0], endDate],
      } as T;
      saveChange(updatedEntity);
      onDateRangeChange?.(currentDateRange[0], endDate);
    },
    [filterDialogOptions, saveChange, onDateRangeChange]
  );

  const handleSelectAll = useCallback(
    (filterGroup: BDFilterModalOptionConfig<T>) => {
      const updatedEntity = {
        ...filterDialogOptions,
        ...getGroupSelection(filterGroup, true),
      } as T;
      saveChange(updatedEntity);
    },
    [filterDialogOptions, saveChange]
  );

  const handleClearAll = useCallback(
    (filterGroup: BDFilterModalOptionConfig<T>) => {
      const updatedEntity = {
        ...filterDialogOptions,
        ...getGroupSelection(filterGroup, false),
      } as T;
      saveChange(updatedEntity);
    },
    [filterDialogOptions, saveChange]
  );

  const handleSetValues = useCallback(
    (filterGroupType: keyof T, values: string[]) => {
      const updatedEntity = {
        ...filterDialogOptions,
        [filterGroupType]: values,
      } as T;
      saveChange(updatedEntity);
    },
    [filterDialogOptions, saveChange]
  );

  const renderSelectAllButton = (filterGroup: BDFilterModalOptionConfig<T>) => {
    return (
      <Box style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <Button
          variant="button-link"
          data-testid={`select-all-${filterGroup.id}`}
          onClick={() => handleSelectAll(filterGroup)}
        >
          <TranslatedLabel
            translationKey="common:selectAll"
            typographyVariant="small_body_regular"
          />
        </Button>
        <Divider orientation="vertical" flexItem className={classes.divider} />
        <Button
          variant="button-link"
          data-testid={`clear-all-${filterGroup.id}`}
          onClick={() => handleClearAll(filterGroup)}
        >
          <TranslatedLabel
            translationKey="common:clearAll"
            typographyVariant="small_body_regular"
          />
        </Button>
      </Box>
    );
  };

  if (type === BDFilterModalType.DROPDOWN) {
    return (
      <Box
        className={classes.accordionContainer}
        data-testid="accordion-filter"
      >
        <Box className={classes.filterContent}>
          {filterOptions.map((filterGroup, index) => (
            <Box
              key={filterGroup.id}
              data-testid={`${filterGroup.id}-filter`}
              className={classes.filterAccordion}
            >
              <Box
                id={`panel${index}-header`}
                style={{ minHeight: 'unset' }}
                className={classes.filterAccordionSummary}
              >
                <Typography variant="overline_semibold">
                  {typeof filterGroup.title === 'string'
                    ? filterGroup.title.toUpperCase()
                    : filterGroup.title}
                </Typography>
              </Box>
              <Box className={classes.filterAccordionDetails}>
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    if (el) groupRefs.current.set(filterGroup.id, el);
                  }}
                  onKeyDown={(e) => handleFilterKeyDown(e, filterGroup.id)}
                  role="group"
                  aria-labelledby={`panel${index}-header`}
                  className={classes.dropdownFilterOptions}
                >
                  {filterGroup.options?.map((option) => (
                    <FormControlLabel
                      key={`${filterGroup.id}-${option.value}`}
                      value={option.value}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          className={classes.checkbox}
                          data-testid={`filter-option-${filterGroup.id}-${String(option.value)}`}
                          checked={
                            !!Object.values(
                              (filterDialogOptions?.[
                                filterGroup.type as keyof T
                              ] || {}) as ElementTypes<T[keyof T]>
                            )?.find((v) => v === option.value)
                          }
                          value={option.value}
                          onChange={(event) => {
                            onChangeFilter(
                              filterGroup.type,
                              event.target.value
                            );
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="small_body_regular"
                          style={{ marginInlineStart: '8px' }}
                        >
                          {option.label}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (type === BDFilterModalType.ACCORDION) {
    return (
      <Box
        className={classes.accordionContainer}
        data-testid="accordion-filter"
      >
        <Box className={classes.filterContent}>
          {showDateRange && (
            <Box data-testid="date-range-filter">
              <Typography variant="overline_semibold">
                {translateText('common:timeStampRange').toUpperCase()}
              </Typography>
              <CSDateRangeFilter
                startDate={
                  (filterDialogOptions as WithDateRange)?.dateRange?.[0] || ''
                }
                endDate={
                  (filterDialogOptions as WithDateRange)?.dateRange?.[1] || ''
                }
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />
            </Box>
          )}
          {filterOptions.map((filterGroup, index) => (
            <Accordion
              key={filterGroup.id}
              defaultExpanded={true}
              data-testid={`accordion-filter-${filterGroup.id}`}
              classes={{
                region: classes.filterAccordion,
                root: classes.filterAccordion,
              }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                style={{ minHeight: 'unset' }}
                className={classes.filterAccordionSummary}
              >
                <Typography variant="overline_semibold">
                  {typeof filterGroup.title === 'string'
                    ? filterGroup.title.toUpperCase()
                    : filterGroup.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.filterAccordionDetails}>
                {filterGroup.enableAllOption &&
                  renderSelectAllButton(filterGroup)}
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    if (el) groupRefs.current.set(filterGroup.id, el);
                  }}
                  onKeyDown={(e) => handleFilterKeyDown(e, filterGroup.id)}
                  role="group"
                  aria-labelledby={`panel${index}-header`}
                  className={
                    singleColumn
                      ? classes.accordionFilterOptionsSingleColumn
                      : classes.accordionFilterOptions
                  }
                >
                  {filterGroup.nestedOptionGroups?.map((nestedGroup) => (
                    <BDFilterNestedOptionGroup
                      key={nestedGroup.id}
                      group={nestedGroup}
                      selected={
                        (filterDialogOptions?.[nestedGroup.type] as
                          | string[]
                          | undefined) ?? []
                      }
                      onToggleOption={onChangeFilter}
                      onSetValues={handleSetValues}
                    />
                  ))}
                  {filterGroup.options?.map((option) => (
                    <FormControlLabel
                      key={`${filterGroup.id}-${option.value}`}
                      value={option.value}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          className={classes.checkbox}
                          data-testid={`filter-option-${filterGroup.id}-${String(option.value)}`}
                          checked={
                            !!Object.values(
                              (filterDialogOptions?.[
                                filterGroup.type as keyof T
                              ] || {}) as ElementTypes<T[keyof T]>
                            )?.find((v) => v === option.value)
                          }
                          value={option.value}
                          onChange={(event) => {
                            onChangeFilter(
                              filterGroup.type,
                              event.target.value
                            );
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="small_body_regular"
                          className={
                            truncateLongLabels
                              ? classes.truncatedFilterOptionLabel
                              : ''
                          }
                          style={{ marginInlineStart: '8px' }}
                        >
                          {option.label}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.contentWrapper}>
      {filterOptions.map((items) => (
        <Box
          key={items.id}
          className="subContentWrapper"
          data-testid={`${items.id}-filter`}
        >
          <Box id={`filter-group-${items.id}`} className={classes.filterLabel}>
            {items.title}
          </Box>
          <Box
            ref={(el: HTMLDivElement | null) => {
              if (el) groupRefs.current.set(items.id, el);
            }}
            onKeyDown={(e) => handleFilterKeyDown(e, items.id)}
            role="group"
            aria-labelledby={`filter-group-${items.id}`}
            className={classes.filterOptions}
          >
            {items.options?.map((option) => (
              <FormControlLabel
                key={`${items.id}-${option.value}`}
                value={option.value}
                control={
                  <Checkbox
                    size="small"
                    checked={
                      !!Object.values(
                        (filterDialogOptions?.[items.type as keyof T] ||
                          {}) as ElementTypes<T[keyof T]>
                      )?.find((v) => v === option.value)
                    }
                    value={option.value}
                    onChange={(event) => {
                      onChangeFilter(items.type, event.target.value);
                    }}
                  />
                }
                label={option.label}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default BDFilterModal;
